import React, { useEffect, useState } from 'react';
import { Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, panelGet, panelPost, useAdminGet } from '../kit';

/**
 * For You · Matching: the background jobs that score problem statements and
 * build keyword pools with the search_v2 LLM relevance. Runs, queue,
 * failures, every pool with a preview, the paid "Re-match all now" /
 * "Rebuild pools" buttons, and the "foryou" step of Search indexes (the
 * monthly-recrawl stand-in).
 */

const S = 'foryou';

const ForYouMatching: React.FC = () => {
  const res = useAdminGet<any>(S, 'matching');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState<any | null>(null);
  const d = res.data || {};
  const q = d.queue || {};
  const c = d.counts || {};
  const est = d.estimate || {};
  const busyQueue = (q.queued || 0) > 0 || (q.active || []).length > 0;

  useEffect(() => {
    if (!busyQueue) return;
    const t = setInterval(() => res.reload(), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyQueue]);

  const act = async (action: string, body: Record<string, any> = {}, done?: (r: any) => string) => {
    setBusy(action); setMsg(null);
    const r = await panelPost(S, action, body);
    setBusy('');
    if (r.error) { setMsg({ kind: 'error', text: r.error }); return; }
    setMsg({ kind: 'ok', text: done ? done(r.data) : 'Done.' });
    res.reload();
  };
  const openPreview = async (keyword: string) => {
    setPreview({ keyword, loading: true });
    const r = await panelGet(S, 'pool_preview', { keyword });
    setPreview(r.error ? { keyword, error: r.error } : { keyword, ...r.data });
  };

  if (res.loading && !res.data) return <Loading />;
  if (res.error) return <Msg kind="error">{res.error}</Msg>;
  const stepRuns = d.step?.runs || [];

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Statements matched" value={c.problems_matched ?? 0} hint={`${c.problems_pending ?? 0} pending · ${c.problems_failed ?? 0} failed`} />
        <KPI label="Keyword pools built" value={c.pools_built ?? 0} hint={`${c.pools_pending ?? 0} pending · ${c.pools_failed ?? 0} failed`} />
        <KPI label="Queue (this process)" value={q.queued ?? 0}
             hint={`${(q.active || []).length} running · ${q.workers ?? 0} workers${q.enabled === false ? ' · FORYOU_WORKER=0' : ''}`} />
        <KPI label="Jobs today (UTC)" value={c.jobs_today ?? 0} />
        <KPI label="LLM calls, 30 days" value={Number(c.llm_calls_30d || 0).toLocaleString()}
             hint={`≈ ${est.per_problem}/statement · ≈ ${est.per_pool}/pool`} />
      </KPIRow>

      <Section title="Run" right={<Btn small onClick={() => res.reload()}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100">
          Paid: every job calls the search_v2 LLM rescore. "Re-match all now" re-queues every statement (current
          matches stay on show until the new ones are written) and rebuilds the pool of every keyword in use — the
          same as the "For You re-match" step in System → Search indexes, which is meant to run after a university load.
        </p>
        <div className="flex gap-2 flex-wrap">
          <DangerBtn onConfirm={() => act('rematch_all', {}, r =>
            `Queued ${r.problems} statements and ${r.pools} pools (~${r.estimated_llm_calls} LLM calls).`)}>
            {busy === 'rematch_all' ? 'Queueing…' : 'Re-match all now'}
          </DangerBtn>
          <DangerBtn onConfirm={() => act('rebuild_pools', {}, r =>
            `Queued ${r.pools} keyword pools (~${r.estimated_llm_calls} LLM calls).`)}>
            {busy === 'rebuild_pools' ? 'Queueing…' : 'Rebuild pools'}
          </DangerBtn>
          <Btn onClick={() => act('retry_failed', {}, r => `Re-queued ${r.problems} statements and ${r.pools} pools.`)}
               disabled={!!busy || !((c.problems_failed || 0) + (c.pools_failed || 0))}>
            Retry failed
          </Btn>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      </Section>

      <Section title={`Search indexes step "foryou"`}>
        {stepRuns.length === 0 ? (
          <p className="text-sm text-grayscale-1100">Not run yet. System → Search indexes → tick "For You re-match (paid LLM)".</p>
        ) : (
          <DataTable rows={stepRuns.map((r: any) => ({
            started_at: r.started_at, by: r.started_by, run_status: r.status,
            step_status: r.result?.status || (r.status === 'running' ? 'waiting' : '—'),
            statements: r.result?.problems, pools: r.result?.pools, llm_calls: r.result?.estimated_llm_calls,
            note: r.result?.note || r.result?.error || '',
          }))} labels={{ started_at: 'Started', llm_calls: 'LLM calls (est.)' }} />
        )}
      </Section>

      <Section title="Queue">
        <DataTable rows={[...(q.active || []).map((a: any) => ({ ...a, state: 'running' })),
                          ...(q.items || []).map((a: any) => ({ ...a, state: 'queued' }))]}
                   columns={['state', 'kind', 'target', 'trigger']} empty="Nothing queued in this process." />
      </Section>

      <Section title="Failures">
        <DataTable rows={d.failures || []} columns={['kind', 'label', 'attempts', 'error', 'next_retry_at']}
                   labels={{ label: 'Statement / keyword', next_retry_at: 'Next retry' }} empty="No failures." />
      </Section>

      <Section title="Keyword pools">
        <DataTable rows={d.pools || []} columns={['keyword', 'members', 'status', 'size', 'built_at', 'attempts', 'error']}
                   labels={{ built_at: 'Built' }} searchable csvName="foryou_pools" empty="No pools yet."
                   actions={row => (
                     <div className="flex gap-1">
                       <Btn small onClick={() => openPreview(row.keyword_norm)}>Preview</Btn>
                       <Btn small disabled={!!busy} onClick={() => act('rebuild_pool', { keyword: row.keyword },
                         () => `Queued a rebuild of "${row.keyword}".`)}>Rebuild</Btn>
                     </div>
                   )} />
      </Section>

      {preview && (
        <Section title={`Pool: ${preview.keyword}`} right={<Btn small onClick={() => setPreview(null)}>Close</Btn>}>
          {preview.loading ? <Loading /> : preview.error ? <Msg kind="error">{preview.error}</Msg> : (
            <DataTable rows={preview.items || []} columns={['rank', 'relevance', 'title', 'university', 'tech_id']}
                       searchable csvName={`foryou_pool_${preview.keyword}`} empty="The pool is empty." />
          )}
        </Section>
      )}

      <Section title="Recent runs">
        <DataTable rows={d.runs || []}
                   columns={['started_at', 'kind', 'target', 'trigger', 'status', 'candidates', 'results', 'llm_calls', 'seconds', 'error']}
                   labels={{ started_at: 'Started', results: 'Kept' }} searchable csvName="foryou_runs" empty="No runs yet." />
      </Section>
    </div>
  );
};

export default ForYouMatching;
